import { Timestamp } from 'firebase/firestore';
import type { WorkSchedule } from '../types/employee';
import { formatLocalDateString } from './dateUtils';

const toDateSafe = (timestamp: unknown): Date => {
  if (!timestamp) return new Date();
  if (timestamp instanceof Date) return timestamp;
  if (
    typeof timestamp === 'object' &&
    timestamp !== null &&
    'toDate' in timestamp &&
    typeof (timestamp as { toDate: () => Date }).toDate === 'function'
  ) {
    return (timestamp as { toDate: () => Date }).toDate();
  }
  if (typeof timestamp === 'string') {
    const parsed = new Date(timestamp);
    return isNaN(parsed.getTime()) ? new Date() : parsed;
  }
  if (typeof timestamp === 'number') {
    return new Date(timestamp);
  }
  return new Date();
};

/** Firestore raw → WorkSchedule (get/snapshot 공통) */
export function mapWorkScheduleFromFirestore(id: string, data: Record<string, unknown>): WorkSchedule {
  let start_time = '';
  let end_time = '';
  if (data.start_time) {
    start_time = toDateSafe(data.start_time).toISOString().slice(0, -1);
  }
  if (data.end_time) {
    end_time = toDateSafe(data.end_time).toISOString().slice(0, -1);
  }

  let scheduleDate = '';
  if (data.date) {
    if (typeof data.date === 'string') {
      scheduleDate = data.date.includes('T') ? data.date.split('T')[0] : data.date;
    } else {
      scheduleDate = formatLocalDateString(toDateSafe(data.date));
    }
  }

  const employeeIdRaw = data.employee_id;
  const employee_id =
    employeeIdRaw === null || employeeIdRaw === undefined ? '' : String(employeeIdRaw);

  const snapshotRaw = data.snapshot_employee_name ?? data.snapshotEmployeeName;
  const snapshot_employee_name =
    typeof snapshotRaw === 'string' && snapshotRaw.trim() ? snapshotRaw.trim() : undefined;

  const schedule: WorkSchedule = {
    id,
    ownerId: (data.ownerId as string) || '',
    date: scheduleDate,
    employee_id,
    employee_name: (data.employee_name as string) || '',
    start_time,
    end_time,
    is_favorite: Boolean(data.is_favorite),
    chat_messages: (data.chat_messages as WorkSchedule['chat_messages']) || [],
    break_time: (data.break_time as string | null) ?? null,
    account_number: (data.account_number as number) || 1,
    mp: data.mp as number,
    isHourly: data.isHourly !== undefined ? Boolean(data.isHourly) : Boolean(data.is_hourly),
    hourlyRate:
      (data.hourlyRate as number | undefined) ?? (data.hourly_rate as number | undefined) ?? 0,
    monthlySalary:
      (data.monthlySalary as number | undefined) ?? (data.monthly_salary as number | undefined) ?? 0,
    weeklyOffDays:
      (data.weeklyOffDays as number | undefined) ?? (data.weekly_off_days as number | undefined) ?? 0,
    weeklyWorkHours:
      (data.weeklyWorkHours as number | undefined) ?? (data.weekly_work_hours as number | undefined) ?? 0,
    taxRate: (data.taxRate as number | undefined) ?? (data.tax_rate as number | undefined) ?? 0,
    dependents: (data.dependents as number) ?? 0,
    hasFourInsurance:
      data.hasFourInsurance === 1 ||
      data.hasFourInsurance === true ||
      data.has_four_insurance === 1 ||
      data.has_four_insurance === true,
    payday: (data.payday as number) ?? 0,
    isweeklyWork:
      data.isweeklyWork !== undefined ? Boolean(data.isweeklyWork) : Boolean(data.is_weekly_work),
    hire_date: data.hire_date,
    severance_pay: (data.severance_pay as number | null) ?? null,
    snapshot_employee_name,
    snapshotEmployeeName: snapshot_employee_name,
    createdAt: (data.createdAt as Timestamp) || Timestamp.now(),
    updatedAt: (data.updatedAt as Timestamp) || Timestamp.now(),
  };

  if (data.start_time_1 && data.end_time_1) {
    schedule.workTime1 = {
      start: toDateSafe(data.start_time_1),
      end: toDateSafe(data.end_time_1),
    };
  }
  if (data.start_time_2 && data.end_time_2) {
    schedule.workTime2 = {
      start: toDateSafe(data.start_time_2),
      end: toDateSafe(data.end_time_2),
    };
  }
  if (data.template_id) {
    schedule.templateId = data.template_id as string;
  }
  if (data.hourly_rate !== undefined) {
    schedule.hourlyRate = data.hourly_rate as number;
  }

  return schedule;
}
